import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Spinner,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Badge,
  Divider,
  Button,
  useToast,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { MdArrowBack, MdCheck, MdShoppingCart } from "react-icons/md";
import { getDetalheCotacao, marcarCompradoAqui } from "../services/api";

const formatarValor = (valor) => {
  if (!valor || valor === "-") return "-";
  const numero = parseFloat(valor);
  if (isNaN(numero)) return valor;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numero);
};

export default function DetalheCotacao() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [cotacao, setCotacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compraLoading, setCompraLoading] = useState({});
  const toast = useToast();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/cotacoes");
    }
  };

  useEffect(() => {
    async function fetchCotacao() {
      try {
        const data = await getDetalheCotacao(token);
        setCotacao(data);
      } catch (error) {
        console.error("Erro ao carregar detalhes da cotação", error);
      } finally {
        setLoading(false);
      }
    }
    fetchCotacao();
  }, [token]);

  const handleCompradoAqui = async (respostaItemId, itemId) => {
    setCompraLoading((s) => ({ ...s, [respostaItemId]: true }));
    try {
      const data = await marcarCompradoAqui(respostaItemId);
      setCotacao((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        next.respostas_cotacao = prev.respostas_cotacao.map((r) => ({
          ...r,
          respostas_itens: r.respostas_itens.map((ri) =>
            ri.id === respostaItemId
              ? { ...ri, comprado_aqui: true, comprado_aqui_em: data.comprado_aqui_em }
              : ri
          ),
        }));
        return next;
      });
      toast({
        title: "Compra registrada",
        description: data.observacao,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || "Erro ao registrar compra.";
      toast({
        title: status === 409 ? "Já estava marcado" : "Erro",
        description: msg,
        status: status === 409 ? "info" : "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCompraLoading((s) => ({ ...s, [respostaItemId]: false }));
    }
  };

  if (loading) {
    return (
      <Flex justify="center" mt={10}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!cotacao) {
    return <Text textAlign="center">Cotação não encontrada.</Text>;
  }

  return (
    <Box
      w="full"
      maxW="1100px"
      mx="auto"
      mt={10}
      px={{ base: 4, md: 6 }}
      py={{ base: 6, md: 8 }}
      bg="white"
      borderRadius="xl"
      shadow="lg"
    >
      <Flex
        direction={{ base: "column", sm: "row" }}
        align={{ base: "stretch", sm: "center" }}
        justify="space-between"
        gap={3}
        mb={6}
      >
        <Button
          onClick={handleBack}
          leftIcon={<MdArrowBack />}
          variant="outline"
          w={{ base: "full", sm: "auto" }}
        >
          Voltar
        </Button>

        <Heading fontSize={{ base: "2xl", md: "3xl" }} textAlign={{ base: "left", sm: "right" }}>
          Cotação #{cotacao.id}
        </Heading>
      </Flex>

      {cotacao.itens.map((item) => (
        <Box key={item.id} mb={10}>
          <Heading size="md" fontSize={{ base: "lg", md: "xl" }} mb={2}>
            {item.nome_produto}
          </Heading>
          <Text>
            Marca desejada: <strong>{item.marca_desejada}</strong>
          </Text>
          <Text>
            Codigo Global: <strong>{item.codigo_global}</strong>
          </Text>
          <Text>
            Quantidade solicitada: <strong>{item.quantidade}</strong>
          </Text>
          <Text>
            Valor de compra: <strong>{formatarValor(item?.valor_compra)}</strong>
          </Text>
          {item.pedido_origem && (
            <Text>
              Pedido de origem: <strong>{item.pedido_origem}</strong>
            </Text>
          )}


          <Box overflowX="auto">
            <Table variant="striped" size="sm" mt={3} minW="900px">
              <Thead bg="gray.100">
                <Tr>
                  <Th>Fornecedor</Th>
                  <Th>Possui Item</Th>
                  <Th>Marca Oferecida</Th>
                  <Th>Qtd Disponível</Th>
                  <Th>Preço</Th>
                  <Th>Observações</Th>
                  <Th>Compra</Th>
                </Tr>
              </Thead>
              <Tbody>
                {cotacao.respostas_cotacao.map((resposta) =>
                  resposta.respostas_itens
                    .filter((ri) => ri.cotacao_item_id === item.id)
                    .map((respostaItem) => {
                      const podeComprar = respostaItem.possui_item && item.pedido_origem;
                      const jaComprado = !!respostaItem.comprado_aqui;
                      return (
                        <Tr key={`${resposta.id}-${respostaItem.id}`}>
                          <Td>{resposta.fornecedor_nome || "Desconhecido"}</Td>
                          <Td>
                            <Badge colorScheme={respostaItem.possui_item ? "green" : "red"}>
                              {respostaItem.possui_item ? "Sim" : "Não"}
                            </Badge>
                          </Td>
                          <Td>{respostaItem.marca_oferecida || "-"}</Td>
                          <Td>{respostaItem.quantidade_disponivel || "-"}</Td>
                          <Td>{formatarValor(respostaItem.preco)}</Td>
                          <Td>{respostaItem.observacoes || "-"}</Td>
                          <Td>
                            {jaComprado ? (
                              <Badge colorScheme="purple" display="inline-flex" alignItems="center" gap={1}>
                                <MdCheck /> Comprado
                              </Badge>
                            ) : (
                              <Button
                                size="xs"
                                colorScheme="purple"
                                variant="outline"
                                leftIcon={<MdShoppingCart />}
                                isDisabled={!podeComprar}
                                isLoading={!!compraLoading[respostaItem.id]}
                                onClick={() => handleCompradoAqui(respostaItem.id, item.id)}
                                title={
                                  !item.pedido_origem
                                    ? "Item sem pedido de origem"
                                    : !respostaItem.possui_item
                                    ? "Fornecedor não tem o item"
                                    : "Marcar como comprado aqui"
                                }
                              >
                                Comprado aqui
                              </Button>
                            )}
                          </Td>
                        </Tr>
                      );
                    })
                )}
              </Tbody>
            </Table>
          </Box>

          <Divider mt={6} />
        </Box>
      ))}
    </Box>
  );
}
